import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { SectionCard } from '@/components/SectionCard/SectionCard'
import { AuditLogDetailPanel } from '@/features/AuditLogs/components/AuditLogDetailPanel/AuditLogDetailPanel'
import { AuditLogsIncompleteNotice } from '@/features/AuditLogs/components/AuditLogsIncompleteNotice/AuditLogsIncompleteNotice'
import { AuditLogsLoadMore } from '@/features/AuditLogs/components/AuditLogsLoadMore/AuditLogsLoadMore'
import { useMergedAuditLogs } from '@/features/AuditLogs/hooks/useMergedAuditLogs'
import type { AuditLogQuery } from '@/features/AuditLogs/lib/auditSourceParams'
import { auditLogsRoute } from '@/features/AuditLogs/pages/AuditLogsPage/AuditLogsPage'
import { LicenseHistoryTable } from '@/features/Licenses/components/LicenseHistoryTable'
import type { AuditLogs } from '@/generated/http-clients/v3ApiSchemas'
import { Box, Button, Icon, Splitter } from '@chakra-ui/react'
import { Link } from '@tanstack/react-router'
import { ExternalLink } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  licenseId: string
}

const SPLITTER_COLLAPSED = [100, 0] as const
const SPLITTER_EXPANDED = [65, 35] as const

const SKELETON_WIDTHS = [150, 200, 300, 100]

export function HistoryPanel({ licenseId }: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })

  const [selectedLog, setSelectedLog] = useState<AuditLogs | null>(null)
  const [splitterSizes, setSplitterSizes] = useState<number[]>([
    ...SPLITTER_COLLAPSED,
  ])
  const isDetailsPanelOpen = splitterSizes[1] > 0

  const query = useMemo<AuditLogQuery>(() => ({ licenseId }), [licenseId])
  const mergedLogs = useMergedAuditLogs(query)

  const handleRowClick = useCallback((log: AuditLogs) => {
    setSelectedLog(log)
    setSplitterSizes((prev) => (prev[1] === 0 ? [...SPLITTER_EXPANDED] : prev))
  }, [])

  const handleCollapseDetails = useCallback(() => {
    setSplitterSizes([...SPLITTER_COLLAPSED])
  }, [])

  return (
    <SectionCard
      w="100%"
      title={String(t('licenses.detail.history.title'))}
      actions={
        <Link
          to={auditLogsRoute.to}
          search={{ licenseId, dateRange: 'allTime' }}
        >
          <Button variant="outline" colorPalette="gray" size="sm">
            <Icon as={ExternalLink} />
            {t('licenses.detail.history.viewInAuditLogs')}
          </Button>
        </Link>
      }
    >
      <Splitter.Root
        size={splitterSizes}
        onResize={(details) => setSplitterSizes(details.size)}
        panels={[
          { id: 'table', minSize: 40 },
          { id: 'details', collapsible: true, collapsedSize: 0, minSize: 20 },
        ]}
        minH="400px"
      >
        <Splitter.Panel id="table">
          <Box
            h="100%"
            display="flex"
            flexDirection="column"
            pr={isDetailsPanelOpen ? 4 : 0}
          >
            {mergedLogs.isIncomplete && <AuditLogsIncompleteNotice />}
            <Box flex="1" overflow="auto">
              <LicenseHistoryTable
                logs={mergedLogs.entries}
                isLoading={mergedLogs.isLoading}
                isError={mergedLogs.isError}
                isEmpty={mergedLogs.isEmpty}
                skeletonWidths={SKELETON_WIDTHS}
                selectedLogId={selectedLog?.id}
                onRowClick={handleRowClick}
                errorComponent={
                  <ApiErrorState
                    apiResource={mergedLogs.errorResource}
                    errorTitle={String(
                      t('licenses.detail.history.error.title'),
                    )}
                    errorDescription={String(
                      t('licenses.detail.history.error.description'),
                    )}
                  />
                }
                emptyMessage={String(t('licenses.detail.history.empty'))}
              />
            </Box>
            {mergedLogs.hasMore && mergedLogs.entries.length > 0 && (
              <AuditLogsLoadMore
                isLoading={mergedLogs.isFetchingMore}
                onLoadMore={mergedLogs.loadMore}
              />
            )}
          </Box>
        </Splitter.Panel>

        {isDetailsPanelOpen && <Splitter.ResizeTrigger id="table:details" />}

        <Splitter.Panel id="details">
          <Box
            h="100%"
            overflow="hidden"
            borderLeftWidth={isDetailsPanelOpen ? '1px' : '0'}
            borderColor="border.muted"
          >
            <AuditLogDetailPanel
              log={selectedLog}
              onCollapse={handleCollapseDetails}
            />
          </Box>
        </Splitter.Panel>
      </Splitter.Root>
    </SectionCard>
  )
}
